import './BillText.scss';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import React, { FunctionComponent, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { serializeToReactElement } from '@enview/interface/utils/RichTextAnnotation';
import TextButton from '../../Shared/Buttons/TextButton';
import {
  openEditUserActionModal,
  openReplyUserActionModal,
  getCardRef,
  setBillSidebarCurrentTab,
} from '../../../dux';
import * as Analytics from '../../../analytics/UserActionAnalytics';
import { State } from '../../../dux/@types';
import { BillSidebarTabs } from '../../../helpers/BillHelper';
import 'react-tooltip/dist/react-tooltip.css';

type BillAnnotationTooltipProps = {
  annotation: UserAction;
};

const REPLY_TEXT = 'Reply';
const JUMP_TO_NOTE = 'Jump to note';

/**
 * Renders the hover popover for a bill annotation. A single instance of this
 * component is rendered per annotation, anchored to every `.annotation-highlight`
 * span that carries a matching `data-annotation-id` (an annotation can span
 * multiple lines, each with its own highlight span) so hovering any line of the
 * annotation shows the popover, not just the first one.
 */
const BillAnnotationTooltip: FunctionComponent<BillAnnotationTooltipProps> = (
  props,
) => {
  const { annotation } = props;
  const orgUser = useSelector((state: State) => state.account.organizationUser);
  const ref = useSelector((state: State) => getCardRef(state, annotation.id));
  const openTab = useSelector((state: State) => state.billView.billSidebarCurrentTab);
  const dispatch = useDispatch();

  const jumpToComment = useCallback(() => {
    Analytics.trackJumpToComment(annotation);
    const delay = openTab === BillSidebarTabs.ACTIVITY ? 0 : 500;
    if (delay > 0) {
      dispatch(setBillSidebarCurrentTab(BillSidebarTabs.ACTIVITY));
    }
    setTimeout(() => {
      if (ref && ref.current) {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, delay);
  }, [dispatch, openTab, annotation, ref]);

  return (
    <ReactTooltip
      anchorSelect={`[data-annotation-id='${annotation.id}']`}
      className="bill-annotation-tooltip tooltip-content"
      clickable
      delayHide={200}
      delayShow={200}
    >
      <>
        <div className="tooltip-options">
          {!annotation.replyTo && (
            <TextButton
              className="tooltip-button"
              label={REPLY_TEXT}
              onClick={() => dispatch(openReplyUserActionModal(annotation))}
            />
          )}
          <TextButton
            className="tooltip-button"
            label={JUMP_TO_NOTE}
            onClick={jumpToComment}
          />
          {orgUser && annotation.organizationUser.id === orgUser.id && (
            <FontAwesomeIcon
              className="edit"
              color="#979797"
              icon={faPen}
              onClick={() => dispatch(openEditUserActionModal(annotation))}
              size="sm"
            />
          )}
        </div>
        <div className="comment">
          {annotation.commentJson ? (
            serializeToReactElement(annotation.commentJson, orgUser?.id)
          ) : (
            <>{annotation.comment}</>
          )}
        </div>
      </>
    </ReactTooltip>
  );
};

export default BillAnnotationTooltip;
